import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { mediaUrl } from "./ProductsPage";
import { fmtDateTime, unitLabel } from "../lib/format";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { ClipboardPlus, Plus, Trash2, X, ChevronsUpDown, Pencil } from "lucide-react";

const ZONES = [["blue", "Blue Zone"], ["green", "Green Zone"]];
const SPACES = [["stand", "Stant"], ["pavilion", "Pavilyon"], ["office", "Ofis"]];
const KINDS = [["__all__", "Tümü"], ["product", "Ürünler"], ["service", "Servisler"], ["depo_disi", "Depo Dışı"]];
const zoneLabel = (z) => (z === "blue" ? "Blue Zone" : z === "green" ? "Green Zone" : z);
const spaceLabel = (s) => Object.fromEntries(SPACES)[s] || s;

export const MBadge = () => (
  <span className="border border-violet-300 bg-violet-50 px-1 py-px text-[9px] font-bold text-violet-700" title="Manuel ekleme" data-testid="manual-badge">M</span>
);

const ProductPicker = ({ products, allProducts, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const expand = (list) => list.flatMap((p) => {
    const vs = p.variants && p.variants.length ? p.variants : [null];
    return vs.map((v) => ({ ...p, variant: v, key: `${p.sku}||${v || ""}` }));
  });
  const items = useMemo(() => expand(products), [products]);
  const pool = useMemo(() => expand(allProducts || products), [allProducts, products]);
  const sel = pool.find((p) => p.key === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className="flex h-10 w-full items-center justify-between rounded-sm border border-stone-300 bg-white px-3 text-left text-sm">
          {sel ? (
            <span className="flex min-w-0 items-center gap-2">
              {sel.image && <img src={mediaUrl(sel.image)} alt="" className="h-6 w-6 shrink-0 border border-stone-200 bg-white object-contain" />}
              <span className="truncate">{sel.name}</span>
              {sel.variant && <span className="shrink-0 border border-stone-300 bg-stone-50 px-1 py-px text-[9px] text-stone-600">{sel.variant}</span>}
              <span className="shrink-0 text-[10px] text-stone-400">{sel.sku}</span>
            </span>
          ) : (
            <span className="text-stone-400">Ürün seçin...</span>
          )}
          <ChevronsUpDown size={13} className="ml-2 shrink-0 text-stone-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command filter={(v, s) => (v.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Ürün adı veya SKU ara..." className="text-xs" />
          <CommandList className="max-h-72">
            <CommandEmpty>Bulunamadı.</CommandEmpty>
            <CommandGroup>
              {items.map((p) => (
                <CommandItem key={p.key} value={`${p.name} ${p.variant || ""} ${p.sku}`} onSelect={() => { onChange({ sku: p.sku, variant: p.variant }); setOpen(false); }} className="text-xs">
                  {p.image ? (
                    <img src={mediaUrl(p.image)} alt="" loading="lazy" className="mr-2 h-8 w-8 shrink-0 border border-stone-200 bg-white object-contain" />
                  ) : (
                    <span className="mr-2 h-8 w-8 shrink-0 border border-stone-100 bg-stone-50" />
                  )}
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate">{p.name}</span>
                      {p.variant && <span className="shrink-0 border border-stone-300 bg-stone-50 px-1 py-px text-[9px] text-stone-600">{p.variant}</span>}
                    </span>
                    <span className="block text-[10px] text-stone-400">{p.sku} · {p.category}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default function ManualOrdersPage() {
  const [allProducts, setAllProducts] = useState([]);
  const [orders, setOrders] = useState(null);
  const [organisation, setOrganisation] = useState("");
  const [zone, setZone] = useState("blue");
  const [spaceType, setSpaceType] = useState("pavilion");
  const [spaceNo, setSpaceNo] = useState("");

  const zoneSpaces = useMemo(() => (zone === "green" ? SPACES.filter(([v]) => v === "stand") : SPACES.filter(([v]) => v !== "stand")), [zone]);

  useEffect(() => {
    if (!zoneSpaces.some(([v]) => v === spaceType)) setSpaceType(zoneSpaces[0][0]);
  }, [zoneSpaces, spaceType]);
  const [kind, setKind] = useState("__all__");
  const [rows, setRows] = useState([{ sku: "", variant: null, qty: "" }]);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editRef, setEditRef] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [formErrors, setFormErrors] = useState([]);

  const load = () => api.get("/manual-orders").then((r) => setOrders(r.data.data)).catch(() => toast.error("Manuel kayıtlar yüklenemedi"));

  useEffect(() => {
    api.get("/products").then((r) => {
      setAllProducts((r.data.data || []).map((p) => ({
        sku: p.sku, name: p.name, category: p.category,
        kind: ["service", "depo_disi"].includes(p.kind) ? p.kind : "product",
        image: (p.images || [])[0],
        variants: (p.options || []).filter((o) => o.active !== false).map((o) => o.label).filter(Boolean),
      })));
    }).catch(() => toast.error("Ürünler yüklenemedi"));
    load();
  }, []);

  const kindProducts = useMemo(() => (kind === "__all__" ? allProducts : allProducts.filter((p) => p.kind === kind)), [allProducts, kind]);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { sku: "", variant: null, qty: "" }]);
  const removeRow = (i) => setRows((rs) => (rs.length === 1 ? [{ sku: "", variant: null, qty: "" }] : rs.filter((_, idx) => idx !== i)));

  const resetForm = () => {
    setOrganisation("");
    setSpaceNo("");
    setRows([{ sku: "", variant: null, qty: "" }]);
    setEditId(null);
    setEditRef(null);
    setFormErrors([]);
  };

  const startEdit = (o) => {
    setEditId(o.id);
    setEditRef(o.ref);
    setOrganisation(o.organisation);
    setZone(o.zone);
    setSpaceType(o.space_type);
    setSpaceNo(o.space_no || "");
    setKind("__all__");
    setRows((o.lines || []).map((l) => ({ sku: l.sku, variant: l.variant || null, qty: String(l.qty) })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    const lines = rows.filter((r) => r.sku && Number(r.qty) > 0).map((r) => ({ sku: r.sku, variant: r.variant || null, qty: Number(r.qty) }));
    const errs = [];
    if (!organisation.trim()) errs.push("Firma adı zorunludur");
    if (!spaceNo.trim()) errs.push(`${spaceLabel(spaceType)} No zorunludur`);
    if (lines.length === 0) errs.push("En az bir ürün satırı (ürün + adet) doldurun");
    setFormErrors(errs);
    if (errs.length) return;
    setSaving(true);
    try {
      const payload = { organisation: organisation.trim(), zone, space_type: spaceType, space_no: spaceNo.trim(), lines };
      if (editId) {
        await api.put(`/manual-orders/${editId}`, payload);
        toast.success("Manuel kayıt güncellendi");
      } else {
        await api.post("/manual-orders", payload);
        toast.success("Manuel kayıt eklendi");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/manual-orders/${removeTarget.id}`);
      toast.success("Kayıt silindi");
      if (editId === removeTarget.id) resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    } finally {
      setRemoveTarget(null);
    }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="manual-orders-page">
      <div className="border border-[#1C4E35]/30 bg-[#1C4E35]/5 p-4" data-testid="manual-entry-card">
        <p className="mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1C4E35]">
          <ClipboardPlus size={14} /> {editId ? `Kaydı Düzenle — ${editRef}` : "Yeni Manuel Kayıt"}
          {editId && (
            <button type="button" onClick={resetForm} data-testid="manual-edit-cancel" className="ml-2 border border-stone-300 bg-white px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-stone-600 hover:bg-stone-100">
              Vazgeç
            </button>
          )}
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Firma Adı <span className="text-red-600">*</span></p>
            <Input value={organisation} onChange={(e) => setOrganisation(e.target.value)} placeholder="Firma / kurum adı" data-testid="manual-organisation" className={`h-10 w-64 rounded-sm bg-white text-sm ${formErrors.length && !organisation.trim() ? "border-red-500" : "border-stone-300"}`} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Bölge <span className="text-red-600">*</span></p>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger className="w-[150px] rounded-sm border-stone-300 bg-white" data-testid="manual-zone-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ZONES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Tür <span className="text-red-600">*</span></p>
            <Select value={spaceType} onValueChange={setSpaceType}>
              <SelectTrigger className="w-[140px] rounded-sm border-stone-300 bg-white" data-testid="manual-space-select"><SelectValue /></SelectTrigger>
              <SelectContent>{zoneSpaces.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">{spaceLabel(spaceType)} No <span className="text-red-600">*</span></p>
            <Input value={spaceNo} onChange={(e) => setSpaceNo(e.target.value)} placeholder={`${spaceLabel(spaceType)} no girin`} data-testid="manual-space-no" className={`h-10 w-40 rounded-sm bg-white text-sm ${formErrors.length && !spaceNo.trim() ? "border-red-500" : "border-stone-300"}`} />
          </div>
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Ürün Tipi</p>
            <div className="flex border border-stone-300 bg-white">
              {KINDS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setKind(v)} data-testid={`manual-kind-${v}`}
                  className={`px-3 py-2.5 text-xs font-medium transition-colors ${kind === v ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 border border-stone-200 bg-white">
          <div className="grid grid-cols-[1fr_120px_40px] items-center gap-2 border-b border-stone-200 bg-stone-50 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-stone-500">
            <span>Ürün ({KINDS.find(([v]) => v === kind)?.[1]})</span>
            <span className="text-right">Adet</span>
            <span />
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_40px] items-center gap-2 border-b border-stone-100 px-3 py-1.5" data-testid={`manual-row-${i}`}>
              <ProductPicker products={kindProducts} allProducts={allProducts} value={r.sku ? `${r.sku}||${r.variant || ""}` : ""} onChange={({ sku, variant }) => setRow(i, { sku, variant })} testId={`manual-row-product-${i}`} />
              <Input type="number" min="1" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} placeholder="0" data-testid={`manual-row-qty-${i}`} className="h-10 rounded-sm border-stone-300 text-right text-sm" />
              <button type="button" onClick={() => removeRow(i)} data-testid={`manual-row-remove-${i}`} className="flex h-8 w-8 items-center justify-center text-stone-400 transition-colors hover:text-red-600">
                <X size={14} />
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between px-3 py-2">
            <button type="button" onClick={addRow} data-testid="manual-add-row" className="flex items-center gap-1.5 border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:border-[#1C4E35] hover:text-[#1C4E35]">
              <Plus size={13} /> Satır Ekle
            </button>
            <button type="button" onClick={save} disabled={saving} data-testid="manual-save" className="bg-[#1C4E35] px-5 py-2 text-xs font-bold text-white transition-colors hover:bg-[#143A26] disabled:opacity-50">
              {saving ? "Kaydediliyor..." : editId ? "Güncelle" : "Tümünü Kaydet"}
            </button>
          </div>
        </div>
        {formErrors.length > 0 && (
          <div className="mt-3 border border-red-300 bg-red-50 px-3 py-2" data-testid="manual-form-errors">
            {formErrors.map((e, i) => (
              <p key={i} className="text-xs font-medium text-red-700">• {e}</p>
            ))}
          </div>
        )}
      </div>

      <div className="border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs">Ref</TableHead>
              <TableHead className="text-xs">Firma</TableHead>
              <TableHead className="text-xs">Bölge</TableHead>
              <TableHead className="text-xs">Tür</TableHead>
              <TableHead className="text-xs">Satırlar</TableHead>
              <TableHead className="text-xs">Ekleyen</TableHead>
              <TableHead className="text-xs">Tarih</TableHead>
              <TableHead className="text-right text-xs">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders !== null && orders.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-6 text-center text-sm text-stone-400" data-testid="manual-empty">Henüz manuel kayıt yok.</TableCell></TableRow>
            )}
            {(orders || []).map((o) => (
              <TableRow key={o.id} data-testid={`manual-order-row-${o.ref}`}>
                <TableCell className="py-2 text-xs font-medium">
                  <span className="flex items-center gap-1.5">{o.ref} <MBadge /></span>
                </TableCell>
                <TableCell className="py-2 text-sm font-medium">{o.organisation}</TableCell>
                <TableCell className="py-2 text-xs">{zoneLabel(o.zone)}</TableCell>
                <TableCell className="py-2 text-xs">{spaceLabel(o.space_type)}{o.space_no ? <span className="text-stone-400"> · No: {o.space_no}</span> : null}</TableCell>
                <TableCell className="py-2">
                  <div className="flex max-w-xl flex-wrap gap-1">
                    {(o.lines || []).map((l, i) => (
                      <span key={i} className="border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-[10px] text-stone-700">
                        {l.name}{l.variant ? ` (${l.variant})` : ""} × <strong>{l.qty} {unitLabel(l.unit)}</strong>
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="py-2 text-xs text-stone-500">{o.created_by}</TableCell>
                <TableCell className="py-2 text-xs text-stone-500">{fmtDateTime(o.created_at)}</TableCell>
                <TableCell className="py-2 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button type="button" onClick={() => startEdit(o)} title="Düzenle" data-testid={`manual-edit-${o.ref}`} className="border border-stone-300 bg-white p-1.5 text-stone-600 transition-colors hover:border-[#1C4E35] hover:text-[#1C4E35]">
                      <Pencil size={12} />
                    </button>
                    <button type="button" onClick={() => setRemoveTarget(o)} data-testid={`manual-delete-${o.ref}`} className="border border-red-300 bg-red-50 p-1.5 text-red-700 transition-colors hover:bg-red-600 hover:text-white">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!removeTarget} onOpenChange={(v) => !v && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="manual-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Manuel kaydı silmek istediğinize emin misiniz?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget && (
                <>
                  <strong>{removeTarget.ref}</strong> — {removeTarget.organisation} kaydı ve içindeki{" "}
                  {(removeTarget.lines || []).length} ürün satırı kalıcı olarak silinecek. Bu işlem geri alınamaz.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manual-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="manual-delete-confirm" className="bg-red-600 text-white hover:bg-red-700">
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
